import { authHeaders, fetchJson, fetchTextOk } from './http'

export function makeCoinsApi() {
  function normPct(v) {
    const p = Math.floor(Number(v))
    if (!Number.isFinite(p) || p < 1 || p > 100) return 0
    return p
  }

  async function createCoin(payload) {
    await fetchJson('/api/coin/new', { method: 'POST', body: JSON.stringify(payload) })
  }

  async function editCoin(mint, patch) {
    const body = {
      mint,
      name:        (patch.name || '').trim(),
      description: (patch.description || '').trim(),
      symbol:      (patch.symbol || '').trim(),
      website:     (patch.website || '').trim(),
      twitter:     (patch.twitter || '').trim(),
      telegram:    (patch.telegram || '').trim(),
      image:       (patch.image || '').trim(),
    }

    await fetchTextOk('/api/coin/edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  async function ownCoin(mint, owner) {
    await fetchTextOk('/api/coin/own', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mint, owner }),
    })
  }

  async function deleteCoin(mint) {
    await fetchJson('/api/coin/delete', { method: 'POST', body: JSON.stringify({ mint }) })
  }

  async function launchCoin(mint, owner, amount = '1_000_000') {
    const body = {
      mint: String(mint || '').trim(),
      owner: String(owner || '').trim(),
      amount: String(amount || '').trim(),
    }
    if (!body.owner || !body.mint) throw new Error('launch_invalid_params')

    const res = await fetch('/api/coin/launch', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(body),
    })

    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(msg || 'launch_failed')
    }

    if (res.status === 204) return {}
    return await res.json().catch(() => ({}))
  }

  async function createTradeBatch(mint, method, owners, extra = {}) {
    const ownersPayload = {}

    if (Array.isArray(owners)) {
      for (const pk of owners) {
        const key = String(pk || '').trim()
        if (!key) continue
        ownersPayload[key] = 10
      }
    } else if (owners && typeof owners === 'object') {
      for (const [pk, value] of Object.entries(owners)) {
        const key = String(pk || '').trim()
        if (!key) continue
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const p = normPct(value.pct ?? value.percent)
          if (p <= 0) continue
          ownersPayload[key] = {
            pct: p,
            buyLamports: Math.floor(Number(value.buyLamports || value.buy_lamports || 0)),
            cup: Math.floor(Number(value.cup || 0)),
            cul: Math.floor(Number(value.cul || 0)),
            takeProfitBps: Math.floor(Number(value.takeProfitBps || 0)),
            takeProfitLamports: Math.floor(Number(value.takeProfitLamports || 0)),
            stopLossBps: Math.floor(Number(value.stopLossBps || 0)),
            maxHoldMs: Math.floor(Number(value.maxHoldMs || 0)),
          }
        } else {
          const p = normPct(value)
          if (p <= 0) continue
          ownersPayload[key] = { pct: p }
        }
      }
    }

    if (!mint || !method || Object.keys(ownersPayload).length === 0) {
      throw new Error('trade_invalid_params')
    }

    const res = await fetch('/api/coin/trade', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ mint, method, owners: ownersPayload, ...(extra || {}) }),
    })

    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(msg || 'trade_create_failed')
    }

    if (res.status === 204) return {}
    return await res.json().catch(() => ({}))
  }

async function createTradeSingle(mint, method, ownerPk, percent) {
  const m = String(mint || '').trim()
  const pk = String(ownerPk || '').trim()
  const p = normPct(percent)

  if (!m || !method || !pk) throw new Error('trade_invalid_params')
  if (p <= 0) throw new Error('trade_invalid_percent')

  return createTradeBatch(m, method, { [pk]: p })
}

async function createGuardedTradeBatch(mint, owners, guard = {}) {
  return createTradeBatch(mint, 'buy', owners, {
    ...(guard || {}),
    guarded: true,
    take_profit_bps: Number(guard.takeProfitBps || 0),
    take_profit_lamports: Number(guard.takeProfitLamports || 0),
    stop_loss_bps: Number(guard.stopLossBps || 0),
    max_hold_ms: Number(guard.maxHoldMs || 0),
  })
}

  return { createCoin, editCoin, ownCoin, deleteCoin, launchCoin, createTradeBatch, createTradeSingle, createGuardedTradeBatch }
}
